import { PageTitleRow } from "../../../components/layout/PageTitleRow";
import { Trans } from "react-i18next"; import { useState, useEffect } from "react";
import { Navigate } from "react-router-dom";
import { ArrowLeft, ChevronRight, FileText, Download } from "lucide-react";
import { AppLayout, PageBackLink } from "../../../components/layout";
import { PageHeader, LoadingState } from "../../../components/ui";
import {
  fetchPendingAnnouncements,
  fetchAnnouncementById,
  publishAnnouncement,
  sendBackAnnouncement,
  saveAnnouncementDocument,
  fetchDepartments,
  fetchManagers,
  sortAnnouncementsForDisplay
} from
  "../../../services/announcementServices";
import { useIsHR } from "../hooks/useIsHR";
import { PriorityPill, StatusPill } from "../components/StatusPill";
import { SendBackModal } from "../components/SendBackModal";
import { Toast } from "../components/Toast";
import { ROUTES, IMPORTANT_COLOR, GENERAL_COLOR, formatDate } from "../constants/announcementOptions";

function RequestCard({ item, onOpen }) {
  return (
    <button type="button" onClick={() => onOpen(item)}
      className="w-full text-left flex items-center justify-between gap-3 bg-white rounded-xl px-6 py-5 border-l-4 shadow-[0_1px_3px_rgba(0,0,0,0.05)] hover:shadow-[0_6px_20px_rgba(30,51,131,0.13)] hover:-translate-y-0.5 transition cursor-pointer"
      style={{ borderLeftColor: item.priorityColor }}>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-3">
          <h3 className="text-[18px] font-medium text-foreground truncate flex-1 min-w-0">{item.title}</h3>
          {item.date && <span className="text-[13px] per-file-hint whitespace-nowrap shrink-0">{item.date}</span>}
        </div>
        <div className="mt-2.5 flex items-center gap-5 text-sm text-foreground flex-wrap">
          <span>{item.createdBy}</span>
          <span className="pl-5 border-l border-(--color-text-border)">
            <PriorityPill priority={item.priority} color={item.priorityColor} />
          </span>
          <span className="pl-5 border-l border-(--color-text-border)">
            {item.audience}
            {item.audience === "Department" && item.departmentName ? ` — ${item.departmentName}` : ""}
            {item.audience === "Reportees" && item.managerName ? ` — Mgr: ${item.managerName}` : ""}
          </span>
        </div>
      </div>
      <ChevronRight className="h-5 w-5 text-accent shrink-0" />
    </button>);

}

function ReviewDetail({ item, onBack, onPublish, onSendBack, onDownload, docDownloading, docDownloadError }) {
  const Field = ({ label, value, children }) =>
    <div>
      <p className="text-sm font-medium per-file-hint mb-1.5">{label}</p>
      {children ?? <p className="text-sm text-foreground">{value}</p>}
    </div>;

  return (
    <>

      <div className="max-w-[900px] bg-white border border-(--color-text-border)/70 rounded-2xl overflow-hidden">
        <div className="px-8 py-6 border-b border-(--color-text-border)/70">
          <div className="flex items-center gap-3 mb-4">
            <PriorityPill priority={item.priority} color={item.priorityColor} />
            <StatusPill status={item.status} />
          </div>
          <PageTitleRow back={<button type="button" onClick={onBack}
        className="flex items-center gap-2 mb-6 text-sm font-semibold text-accent hover:underline">
        <ArrowLeft className="h-4 w-4" /> <Trans i18nKey="formActions.backToRequests" />
      </button>}>
            <h2 className="text-2xl font-bold text-foreground wrap-break-word">{item.title}</h2>
          </PageTitleRow>
        </div>

        <div className="px-8 py-6 border-b border-(--color-text-border)/70 grid grid-cols-1 sm:grid-cols-2 gap-5">
          <Field label="Date" value={item.date ? item.date.toUpperCase() : "—"} />
          <Field label="Archive Date" value={item.archiveDate ? item.archiveDate.toUpperCase() : "—"} />
          <Field label="Created By" value={`${item.createdBy} · ${item.createdByRole}`} />
          <Field label="Recipients">
            <p className="text-sm text-foreground">
              {item.audience || "—"}
              {item.audience === "Department" && item.departmentName &&
                <span className="per-file-hint"> — {item.departmentName}</span>
              }
              {item.audience === "Reportees" && item.managerName &&
                <span className="per-file-hint"> <Trans i18nKey="announcements.mgr" /> {item.managerName}</span>
              }
            </p>
          </Field>
        </div>

        <div className="px-8 py-6 border-b border-(--color-text-border)/70">
          <p className="text-sm font-medium per-file-hint"><Trans i18nKey="announcements.content" /></p>
          <p className="text-sm leading-relaxed text-foreground mt-2.5 whitespace-pre-line wrap-break-word">{item.content}</p>
        </div>

        <div className="px-8 py-6 border-b border-(--color-text-border)/70">
          <p className="text-sm font-medium per-file-hint"><Trans i18nKey="common.attachments" /></p>
          {item.attachment ?
            <div className="flex items-center gap-3.5 mt-3">
              <span className="grid place-items-center h-9 w-9 rounded-md bg-(--status-archived-bg) text-foreground shrink-0">
                <FileText className="h-4 w-4" />
              </span>
              <span className="flex flex-col flex-1 min-w-0">
                <span className="text-sm font-medium text-foreground">{item.attachment.name}</span>
                <span className="text-xs per-file-hint mt-0.5">{item.attachment.size} {item.attachment.note}</span>
              </span>
              <button type="button" onClick={() => onDownload(item.announcementId)} disabled={docDownloading}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-(--color-text-border) bg-white text-sm font-medium text-foreground hover:bg-workspace disabled:opacity-60">
                <Download className="h-4 w-4" />
                {docDownloading ? "Downloading…" : "Download"}
              </button>
            </div> :

            <p className="mt-2 text-sm per-file-hint italic"><Trans i18nKey="announcements.noDocumentsAttached" /></p>
          }
          {docDownloadError && <p className="mt-1.5 text-[13px] text-error">{docDownloadError}</p>}
        </div>

        <div className="flex justify-end gap-3 px-8 py-5 bg-[#f8fafc]">
          <button type="button" onClick={() => onSendBack(item)}
            className="h-10 px-6 rounded-xl border border-(--color-text-border) bg-white text-sm font-medium text-foreground hover:bg-workspace">
            <Trans i18nKey="formActions.sendBack" />
          </button>
          <button type="button" onClick={() => onPublish(item)}
            className="h-10 px-6 rounded-xl bg-primary text-white text-sm font-medium hover:bg-accent">
            <Trans i18nKey="formActions.publish" />
          </button>
        </div>
      </div>
    </>);

}

export function HRReviewRequestsPage() {
  const { isHR } = useIsHR();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openItem, setOpenItem] = useState(null);
  const [sendBackTarget, setSendBackTarget] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [successToast, setSuccessToast] = useState(null);
  const [docDownloading, setDocDownloading] = useState(false);
  const [docDownloadError, setDocDownloadError] = useState(null);

  useEffect(() => {
    fetchPendingAnnouncements()
      .then(async (items) => {
        const normalised = sortAnnouncementsForDisplay(items).map((item) => ({
          id: String(item.announcementId ?? Math.random()),
          announcementId: item.announcementId,
          title: item.announcementTitle ?? "",
          createdBy: !isNaN(Number(item.createdBy)) ? item.createdByName ?? item.employeeName ?? "Admin" : item.createdBy ?? "Admin",
          createdByRole: item.designationName ?? (item.designationId ? "Manager" : "HR"),
          priority: item.announcementType ?? "General",
          priorityColor: (item.announcementType ?? "").toLowerCase() === "important" ? IMPORTANT_COLOR : GENERAL_COLOR,
          audience: item.recipients ?? "",
          departmentId: item.departmentId ?? 0,
          managerId: item.managerId ?? 0,
          date: formatDate(item.createdOn),
          archiveDate: formatDate(
            item.archiveDate ?? item.announcementArchiveDate ?? item.expiryOn ?? item.endDate ?? item.validUntil ?? item.expiry ?? item.announcementExpiry ?? item.expirationDate ?? item.validTill ?? null
          ),
          status: item.announcementStatus ?? "Pending",
          content: item.announcementBody ?? "",
          attachment: item.hasSupportingDocument ? { name: "Supporting Document", size: "Unknown", note: "Viewable on approval" } : null,
          departmentName: item.departmentName ?? item.deptName ?? item.department_name ?? null,
          managerName: item.managerName ?? item.managerFirstName ?? item.manager_name ?? null
        }));
        const hasDept = normalised.some((i) => i.audience === "Department" && i.departmentId > 0 && !i.departmentName);
        const hasMgr = normalised.some((i) => i.audience === "Reportees" && i.managerId > 0 && !i.managerName);
        let deptMap = {}, mgrMap = {};
        if (hasDept) {
          const d = await fetchDepartments().catch(() => []);
          deptMap = Object.fromEntries(d.map((x) => [x.departmentId, x.departmentName]));
        }
        if (hasMgr) {
          const m = await fetchManagers().catch(() => []);
          mgrMap = Object.fromEntries(m.map((x) => [x.managerId, x.managerName]));
        }
        setRequests(normalised.map((i) => ({
          ...i,
          departmentName: i.departmentName ?? (i.audience === "Department" && i.departmentId > 0 ? deptMap[i.departmentId] ?? null : null),
          managerName: i.managerName ?? (i.audience === "Reportees" && i.managerId > 0 ? mgrMap[i.managerId] ?? null : null)
        })));
      })
      .catch((err) => setError(err.status === 401 ?
        "Session expired. Please refresh the page and log in again." :
        "Failed to load requests. Please try again."))
      .finally(() => setLoading(false));
  }, []);

  const handlePublish = async (item) => {
    try {
      setActionLoading(true);
      setActionError(null);
      await publishAnnouncement(item.announcementId, "", {
        announcementTitle: item.title,
        announcementBody: item.content,
        announcementType: item.announcementType,
        recipients: item.audience,
        departmentId: item.departmentId ?? 0,
        managerId: item.managerId ?? 0,
        archiveDate: item.archiveDateISO ?? "",
        publishDate: item.publishDateISO ?? "",
        supportingDocumentBase64: item.supportingDocumentBase64 ?? ""
      });
      setRequests((prev) => prev.filter((r) => r.announcementId !== item.announcementId));
      setOpenItem(null);
      setSuccessToast({ type: "published", text: "Announcement published successfully!" });
    } catch (err) {
      setActionError(err.status === 401 ? "Session expired. Please refresh and log in again." : `Publish failed: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const confirmSendBack = async (remarks) => {
    const target = sendBackTarget;
    try {
      setActionLoading(true);
      setActionError(null);
      await sendBackAnnouncement(target.announcementId, remarks, {
        announcementTitle: target.title,
        announcementBody: target.content,
        announcementType: target.announcementType,
        recipients: target.audience,
        departmentId: target.departmentId ?? 0,
        managerId: target.managerId ?? 0,
        archiveDate: target.archiveDateISO ?? "",
        publishDate: target.publishDateISO ?? "",
        supportingDocumentBase64: target.supportingDocumentBase64 ?? ""
      });
      setRequests((prev) => prev.filter((r) => r.announcementId !== target.announcementId));
      setOpenItem(null);
      setSendBackTarget(null);
      setSuccessToast({ type: "sent-back", text: "Sent back to creator for corrections." });
    } catch (err) {
      setActionError(err.status === 401 ? "Session expired. Please refresh and log in again." : `Send back failed: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDownloadDoc = async (announcementId) => {
    setDocDownloading(true);
    setDocDownloadError(null);
    try {
      await saveAnnouncementDocument(announcementId);
    } catch (err) {
      setDocDownloadError(`Download failed: ${err.message}`);
    } finally {
      setDocDownloading(false);
    }
  };

  const handleOpenDetail = async (listItem) => {
    setDetailLoading(true);
    try {
      const raw = await fetchAnnouncementById(listItem.announcementId);
      const expiryRaw = raw?.archiveDate ?? raw?.announcementArchiveDate ?? raw?.expiryOn ?? raw?.endDate ?? raw?.validUntil ?? null;
      const publishRaw = raw?.publishDate ?? raw?.announcementPublishDate ?? raw?.publishOn ?? null;
      const audience = raw?.recipients ?? listItem.audience;
      const deptId = raw?.departmentId ?? 0;
      const mgrId = raw?.managerId ?? 0;
      let departmentName = raw?.departmentName ?? raw?.deptName ?? raw?.department_name ?? null;
      let managerName = raw?.managerName ?? raw?.managerFirstName ?? raw?.manager_name ?? null;
      if (!departmentName && audience === "Department" && deptId > 0) {
        const depts = await fetchDepartments().catch(() => []);
        departmentName = depts.find((d) => d.departmentId === deptId)?.departmentName ?? null;
      }
      if (!managerName && audience === "Reportees" && mgrId > 0) {
        const mgrs = await fetchManagers().catch(() => []);
        managerName = mgrs.find((m) => m.managerId === mgrId)?.managerName ?? null;
      }
      setOpenItem({
        ...listItem,
        title: raw?.announcementTitle ?? listItem.title,
        content: raw?.announcementBody ?? listItem.content,
        audience,
        date: formatDate(raw?.createdOn) || listItem.date,
        archiveDate: formatDate(expiryRaw) || listItem.archiveDate,
        status: raw?.announcementStatus ?? listItem.status,
        attachment: raw?.hasSupportingDocument ? { name: "Supporting Document", size: "Unknown", note: "Viewable on approval" } : null,
        departmentName,
        managerName,
        announcementType: raw?.announcementType ?? "",
        departmentId: deptId,
        managerId: mgrId,
        archiveDateISO: expiryRaw ?? "",
        publishDateISO: publishRaw ?? "",
        supportingDocumentBase64: raw?.supportingDocumentBase64 ?? ""
      });
    } catch {
      setOpenItem(listItem);
    } finally {
      setDetailLoading(false);
    }
  };

  // Non-HR users shouldn't reach this page directly.
  if (!isHR) return <Navigate to={ROUTES.creatorList} replace />;

  const heading = openItem ? "Review Announcement" : "Review Requests";

  return (
    <AppLayout>
      {/* List view backs out to Announcements; the detail view has its own
           "Back to requests" link inside <ReviewDetail>. */}
      <PageHeader back={!openItem && <PageBackLink to={ROUTES.creatorList} label="Back to Announcements" />} title={heading} />

      {detailLoading ?
        <LoadingState /> :
        openItem ?
          <ReviewDetail
            item={openItem}
            onBack={() => setOpenItem(null)}
            onPublish={() => handlePublish(openItem)}
            onSendBack={() => setSendBackTarget(openItem)}
            onDownload={handleDownloadDoc}
            docDownloading={docDownloading}
            docDownloadError={docDownloadError} /> :

          loading ?
            <LoadingState /> :
            error ?
              <p className="text-sm text-error">{error}</p> :
              requests.length === 0 ?
                <p className="text-sm per-file-hint"><Trans i18nKey="announcements.noPendingRequests" /></p> :

                <div className="max-w-[1050px] flex flex-col gap-4">
                  {requests.map((item) =>
                    <RequestCard key={item.id} item={item} onOpen={handleOpenDetail} />
                  )}
                </div>
      }

      {actionError &&
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-400 bg-(--status-rejected-bg) text-(--status-rejected-fg) px-6 py-3 rounded-xl shadow-lg text-sm">
          {actionError}
          <button onClick={() => setActionError(null)} className="ml-3 font-bold">✕</button>
        </div>
      }

      {successToast &&
        <Toast tone={successToast.type === "published" ? "success" : "sent-back"} onDone={() => setSuccessToast(null)} duration={5000}>
          {successToast.type === "published" ? "✓" : "↩"} {successToast.text}
        </Toast>
      }

      <SendBackModal
        open={Boolean(sendBackTarget)}
        onConfirm={confirmSendBack}
        onClose={() => setSendBackTarget(null)}
        loading={actionLoading} />

    </AppLayout>);

}
